import { Box, ListItemIcon, MenuItem } from "@mui/material";
import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { MenuOptionType } from "./models/MenuOptionType";

interface ContextMenuProps {
	show: boolean;
	position: { x: number; y: number };
	options: MenuOptionType[];
}

export const ContextMenu = ({ show, position, options }: ContextMenuProps) => {
	const menuRef = useRef<HTMLUListElement>(null);

	// Seeded with the click position rather than 0: even before it is measured the menu is already
	// where the pointer is, so nothing ever paints in the corner of the screen.
	const [x, setX] = useState(`${position.x}px`);
	const [y, setY] = useState(`${position.y}px`);
	// useLayoutEffect, not useEffect: this runs before the browser paints. With useEffect the menu
	// was painted once at its initial position and only then moved, which showed as a flicker in
	// the top left corner.
	useLayoutEffect(() => {
		const positionDiv = () => {
			if (show && menuRef.current) {
				const divRect = menuRef.current.getBoundingClientRect();
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;

				let left = position.x;
				let top = position.y;

				if (left + divRect.width > viewportWidth) {
					left = viewportWidth - divRect.width;
				}

				if (top + divRect.height > viewportHeight) {
					top = viewportHeight - divRect.height;
				}

				setX(`${left}px`);
				setY(`${top}px`);
			} else {
				setX(`${position.x}px`);
				setY(`${position.y}px`);
			}
		};

		positionDiv();
	}, [show, position.x, position.y]);

	if (!show) return null;

	return (
		<Box
			component="ul"
			ref={menuRef}
			sx={{
				position: "fixed",
				opacity: show ? 1 : 0,
				left: x,
				top: y,
				backgroundColor: "#fff",
				borderRadius: "2px",
				padding: "5px 0",
				width: "auto",
				height: "auto",
				margin: "0",
				zIndex: (theme) => theme.zIndex.modal + 1,
				listStyle: "none",
				boxShadow: "0 0 20px 0 #ccc",
				"& .MuiMenuItem-root": {
					fontSize: "0.875rem",
				},
				"& .MuiListItemIcon-root": {
					minWidth: "25px",
					padding: "0",
				},
				"& .MuiSvgIcon-root": {
					fontSize: "1rem",
				},
			}}
		>
			{options.map((option, i) => (
				<MenuItem
					key={option.key + i}
					style={option.style}
					onClick={option.onClick}
					disabled={option.disabled}
					data-test-id={option.key}
				>
					<ListItemIcon style={{ padding: 0, margin: 0 }}>
						{option.icon}
					</ListItemIcon>
					{option.name}
				</MenuItem>
			))}
		</Box>
	);
};
